import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary to-primary-dark text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-4">Privacy Policy</h1>
          <p className="text-xl text-white/90">
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to ACT PAWS ("we," "our," or "us"). We are committed to protecting your privacy 
              and ensuring the security of your personal information. This Privacy Policy explains how we 
              collect, use, disclose, and safeguard your information when you use our pet activity tracking 
              application and services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using ACT PAWS, you agree to the collection and use of information in accordance with this 
              policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-2xl font-semibold text-primary mb-3 mt-6">2.1 Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-3">We may collect the following types of personal information:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Name and contact information (email address, phone number)</li>
              <li>Account credentials (username, password)</li>
              <li>Profile information and preferences</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
            </ul>

            <h3 className="text-2xl font-semibold text-primary mb-3 mt-6">2.2 Pet Information</h3>
            <p className="text-gray-700 leading-relaxed mb-3">To provide our services, we collect information about your pets, including:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Pet name, breed, age, weight, and species</li>
              <li>Health records, vaccinations, and medical history</li>
              <li>Activity data, exercise routines, and fitness metrics</li>
              <li>Daily, weekly, and monthly activity logs</li>
              <li>Photos and other media you upload</li>
            </ul>

            <h3 className="text-2xl font-semibold text-primary mb-3 mt-6">2.3 Usage Data</h3>
            <p className="text-gray-700 leading-relaxed mb-3">We automatically collect certain information when you use our app:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Device information (device type, operating system, unique device identifiers)</li>
              <li>App usage patterns and features accessed</li>
              <li>Log data (IP address, access times, pages viewed)</li>
              <li>Location data (if you grant permission)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We use the collected information for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>To provide, maintain, and improve our pet tracking services</li>
              <li>To create and manage your account and pet profiles</li>
              <li>To track and analyze your pet's health, fitness, and activity data</li>
              <li>To generate insights, progress reports, and recommendations</li>
              <li>To send you notifications, updates, and important information about your pets</li>
              <li>To process payments and manage subscriptions</li>
              <li>To respond to your inquiries, requests, and provide customer support</li>
              <li>To detect, prevent, and address technical issues and security threats</li>
              <li>To comply with legal obligations and enforce our terms of service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We do not sell your personal information. We may share your information in the following circumstances:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Service Providers:</strong> We may share data with trusted third-party service providers who assist us in operating our app, conducting business, or serving users (e.g., cloud storage, analytics, payment processing).</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or government regulation, or to protect our rights, property, or safety.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
              <li><strong>With Your Consent:</strong> We may share information with your explicit consent or at your direction.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. These measures 
              include encryption, secure servers, access controls, and regular security assessments.
            </p>
            <p className="text-gray-700 leading-relaxed">
              However, no method of transmission over the internet or electronic storage is 100% secure. 
              While we strive to use commercially acceptable means to protect your data, we cannot guarantee 
              absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined 
              in this Privacy Policy, unless a longer retention period is required or permitted by law. 
              When you delete your account, we will delete or anonymize your personal information in 
              accordance with our data retention policies.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Your Rights and Choices</h2>
            <p className="text-gray-700 leading-relaxed mb-3">You have the following rights regarding your personal information:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Access:</strong> You can request access to the personal information we hold about you.</li>
              <li><strong>Correction:</strong> You can update or correct your personal information through the app settings.</li>
              <li><strong>Deletion:</strong> You can request deletion of your account and associated data (see our Delete Account page).</li>
              <li><strong>Data Portability:</strong> You can request a copy of your data in a portable format.</li>
              <li><strong>Opt-Out:</strong> You can opt-out of certain communications and data processing activities.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are not intended for children under the age of 13. We do not knowingly collect 
              personal information from children under 13. If you believe we have collected information 
              from a child under 13, please contact us immediately, and we will take steps to delete 
              such information.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country 
              of residence. These countries may have data protection laws that differ from those in your 
              country. By using our services, you consent to the transfer of your information to these countries.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date. You are 
              advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-3">If you have any questions about this Privacy Policy, please contact us:</p>
            <ul className="space-y-2 text-gray-700">
              <li><strong>Email:</strong> <a href="mailto:support@actpaws.com" className="text-primary hover:underline">support@actpaws.com</a></li>
              <li><strong>Phone:</strong> <a href="tel:+919980363932" className="text-primary hover:underline">+91 9980363932</a></li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
